import { Button, Icon } from "@legal-500/legal500-design-system";
import { useEffect, useRef } from "react";
import styles from "./SkipProductsModal.module.css";

export type SkipProduct = {
  productId: string;
  logCount: number;
};

export type SkipProductsModalProps = {
  products: SkipProduct[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
};

export function SkipProductsModal({
  products,
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
}: SkipProductsModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !isSubmitting) {
        onClose();
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen || products.length === 0) return null;

  function handleBackdropClick(event: React.MouseEvent) {
    if (event.target === event.currentTarget && !isSubmitting) {
      onClose();
    }
  }

  const totalLogs = products.reduce((sum, p) => sum + p.logCount, 0);

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="skip-products-title"
      >
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <h2 id="skip-products-title" className={styles.modalTitle}>
              Skip Products
            </h2>
          </div>
          <button
            className={styles.modalCloseButton}
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <Icon icon="cross" size="small" />
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.message}>
            Create skip mappings for {products.length} unique product
            {products.length !== 1 ? "s" : ""} from {totalLogs} selected log
            {totalLogs !== 1 ? "s" : ""}. Future webhooks with these product IDs will be logged as
            &quot;dropped&quot; instead of failing.
          </p>

          <div className={styles.productList}>
            {products.map(product => (
              <div key={product.productId} className={styles.productItem}>
                <span className={styles.productId}>{product.productId}</span>
                <span className={styles.logCount}>
                  {product.logCount} log{product.logCount !== 1 ? "s" : ""}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={onConfirm} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className={styles.spinner} />
                Skipping...
              </>
            ) : (
              `Skip ${products.length} Product${products.length !== 1 ? "s" : ""}`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
